import { createSlice } from '@reduxjs/toolkit';
import { message as toaster } from 'antd';
import { API } from 'api';

const initialState = {};

export const employeeSliceV2 = createSlice({
	name: 'employeeStateV2',
	initialState,
	reducers: {},
});

// Constants
const ERROR_MSG_EMPLOYEE_DATA_REQUIRED = 'Employee data is required';
const ERROR_MSG_EMPLOYEE_ID_REQUIRED = 'Employee ID is required for update';
const ERROR_MSG_EMPLOYEE_ID_REQUIRED_STATUS = 'Employee ID is required';

/**
 * Helper: Clean filter_by object by removing undefined/null/empty values
 * @param {Object} filter_by - Filter object
 * @returns {Object} Cleaned filter object
 */
const cleanFilterBy = (filter_by = {}) => {
	const cleaned = {};
	if (filter_by) {
		Object.keys(filter_by).forEach((key) => {
			if (filter_by[key] !== undefined && filter_by[key] !== null && filter_by[key] !== '') {
				// Handle arrays - include if not empty
				if (Array.isArray(filter_by[key])) {
					if (filter_by[key].length > 0) {
						cleaned[key] = filter_by[key];
					}
				} else {
					cleaned[key] = filter_by[key];
				}
			}
		});
	}
	return cleaned;
};

/**
 * Helper: Transform V2 search response to V1 format for RCTable compatibility
 * @param {Object} response - V2 API response
 * @returns {Object} Transformed response in V1 format
 */
const transformSearchResponse = (response) => {
	if (!response || response.status !== 'success') {
		return {
			rows: [],
			count: 0,
			cities: [],
			clients: [],
			projects: [],
		};
	}

	return {
		rows: response.data || [],
		count: response.total_count || 0,
		cities: [], // Will be fetched separately via getEmployeeFiltersV2
		clients: [], // Will be fetched separately via getEmployeeFiltersV2
		projects: [], // Will be fetched separately via getEmployeeFiltersV2
	};
};

/**
 * Helper: Transform clients/projects objects to array format for Select components
 * @param {Object} obj - Object with id => name/code mapping
 * @returns {Array} Array of { value, label } objects
 */
const transformObjectToOptions = (obj) => {
	if (!obj || typeof obj !== 'object') return [];
	return Object.entries(obj).map(([id, label]) => ({
		value: id,
		label,
	}));
};

/**
 * Search Employees V2
 * @param {Object} params
 * @param {Object} params.filter_by - Filter criteria
 * @param {number} params.offset - Pagination offset (default: 0)
 * @param {number} params.limit - Results per page (default: 20, max: 1000)
 * @param {boolean} params.fetch_filters - Whether to fetch filter options (default: false)
 * @param {Object} params.filter_list - Filter list object (default: {})
 * @returns {Promise} Object with { rows, count, cities, clients, projects }
 */
export const searchEmployeeV2 =
	({
		filter_by = {},
		offset = 0,
		limit = 20,
		fetch_filters = false,
		filter_list = {},
		...rest
	}) =>
	async () => {
		// Clean filter_by - remove offset and limit if they were accidentally included
		const cleanFilterByObj = cleanFilterBy({
			...filter_by,
			...rest,
		});

		// Validate and clamp limit
		const validatedLimit = Math.min(Math.max(Number(limit) || 20, 1), 1000);
		const validatedOffset = Number(offset) || 0;

		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'employee',
				offset: validatedOffset,
				limit: validatedLimit,
				fetch_filters: Boolean(fetch_filters),
				filter_list: filter_list || {},
				filter_by: cleanFilterByObj,
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then(transformSearchResponse);
	};

/**
 * Helper: Check if user type requires employee_info
 * @param {string} userType - User type
 * @returns {boolean} True if employee_info is required
 */
const requiresEmployeeInfo = (userType) => {
	if (!userType) return false;
	const excludedTypes = ['admin', 'business', 'finance'];
	return !excludedTypes.includes(userType);
};

/**
 * Helper: Build employee_info object from form data
 * @param {Object} employeeInfo - Employee info from form
 * @returns {Object|null} Cleaned employee_info or null
 */
const buildEmployeeInfo = (employeeInfo) => {
	if (!employeeInfo) return null;

	const info = {};

	// Include project_ids if available and is array
	if (Array.isArray(employeeInfo.project_ids) && employeeInfo.project_ids.length > 0) {
		info.project_ids = employeeInfo.project_ids;
	}

	// Include cities if available and is array
	if (Array.isArray(employeeInfo.cities) && employeeInfo.cities.length > 0) {
		info.cities = employeeInfo.cities;
	}

	// Include business_unit_ids if available and is array
	if (
		Array.isArray(employeeInfo.business_unit_ids) &&
		employeeInfo.business_unit_ids.length > 0
	) {
		info.business_unit_ids = employeeInfo.business_unit_ids;
	}

	// Return null if empty to avoid sending empty object
	return Object.keys(info).length > 0 ? info : null;
};

/**
 * Helper: Build clean employee data payload for V2 API
 * Removes form-only fields and ensures structure matches API contract
 * @param {Object} formData - Raw form data
 * @returns {Object|null} Cleaned employee data matching API contract
 */
const buildEmployeeCreatePayload = (formData) => {
	if (!formData) {
		return null;
	}

	// Build base employee data - only include fields from API contract
	const employeeData = {
		username: formData.username,
		password: formData.password,
		user_type: formData.user_type,
		first_name: formData.first_name,
		last_name: formData.last_name,
		email: formData.email,
		employee_code: formData.employee_code,
		mobile_number: formData.mobile_number,
		...(formData.alternate_number && { alternate_number: formData.alternate_number }),
		designation: formData.designation,
		...(formData.profile_filename && { profile_filename: formData.profile_filename }),
		...(formData.is_active !== undefined && { is_active: formData.is_active }),
	};

	// Add employee_info for non-admin/finance/business user types
	if (requiresEmployeeInfo(formData.user_type) && formData.employee_info) {
		const employeeInfo = buildEmployeeInfo(formData.employee_info);
		if (employeeInfo) {
			employeeData.employee_info = employeeInfo;
		}
	}

	return employeeData;
};

/**
 * Helper: Build clean employee update payload for V2 API
 * Excludes username (cannot be changed) and only includes password if provided
 * @param {Object} formData - Raw form data
 * @returns {Object|null} Cleaned employee update data matching API contract
 */
const buildEmployeeUpdatePayload = (formData) => {
	if (!formData) {
		return null;
	}

	if (!formData.id) {
		return null;
	}

	// Build base employee data - exclude username (cannot be changed in V2)
	const employeeData = {
		id: formData.id,
		user_type: formData.user_type,
		first_name: formData.first_name,
		last_name: formData.last_name,
		email: formData.email,
		employee_code: formData.employee_code,
		mobile_number: formData.mobile_number,
		...(formData.alternate_number && { alternate_number: formData.alternate_number }),
		designation: formData.designation,
		...(formData.profile_filename && { profile_filename: formData.profile_filename }),
		...(formData.is_active !== undefined && { is_active: formData.is_active }),
		// Password is optional - only include if provided (user wants to change password)
		...(formData.password &&
			formData.password.trim() !== '' && { password: formData.password }),
	};

	// Add employee_info for non-admin/finance/business user types
	if (requiresEmployeeInfo(formData.user_type) && formData.employee_info) {
		const employeeInfo = buildEmployeeInfo(formData.employee_info);
		if (employeeInfo) {
			employeeData.employee_info = employeeInfo;
		}
	}

	return employeeData;
};

/**
 * Create Employee V2
 * @param {Object} params
 * @param {Object} params.data - Employee form data
 * @param {Function} params.setErrors - Form error setter function
 * @returns {Promise} API response with created employee data
 */
export const createEmployeeV2 =
	({ data, setErrors }) =>
	async () => {
		if (!data) {
			toaster.error(ERROR_MSG_EMPLOYEE_DATA_REQUIRED);
			return Promise.reject(new Error(ERROR_MSG_EMPLOYEE_DATA_REQUIRED));
		}

		// Build clean payload matching API contract
		const cleanedData = buildEmployeeCreatePayload(data);

		if (!cleanedData) {
			toaster.error(ERROR_MSG_EMPLOYEE_DATA_REQUIRED);
			return Promise.reject(new Error(ERROR_MSG_EMPLOYEE_DATA_REQUIRED));
		}

		const payload = {
			create_type: 'employee',
			data: cleanedData,
		};

		const CREDENTIALS = {
			url: '/create',
			data: payload,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res?.status === 'success') {
				toaster.success(res?.message || 'Employee created successfully');
				// Return in V1 format for compatibility
				return {
					data: res.data,
				};
			}
			// Handle error response - API.common will handle setErrors via handle422Error
			// Reject to trigger catch in form's handleSubmit
			if (res?.status === 'error' && res?.error) {
				// Error already handled by API.common, just reject
				return Promise.reject(new Error(res.error.message || 'Failed to create employee'));
			}
			return Promise.reject(new Error('Unexpected response format'));
		});
	};

/**
 * Update Employee V2
 * @param {Object} params
 * @param {Object} params.data - Employee data to update (must include id field)
 * @param {Function} params.setErrors - Form error setter function
 * @returns {Promise} API response with updated employee data
 */
export const updateEmployeeV2 =
	({ data, setErrors }) =>
	async () => {
		if (!data) {
			toaster.error(ERROR_MSG_EMPLOYEE_DATA_REQUIRED);
			return Promise.reject(new Error(ERROR_MSG_EMPLOYEE_DATA_REQUIRED));
		}

		if (!data.id) {
			toaster.error(ERROR_MSG_EMPLOYEE_ID_REQUIRED);
			return Promise.reject(new Error(ERROR_MSG_EMPLOYEE_ID_REQUIRED));
		}

		// Build clean payload - exclude username (cannot be changed in V2)
		const cleanedData = buildEmployeeUpdatePayload(data);

		if (!cleanedData) {
			toaster.error(ERROR_MSG_EMPLOYEE_DATA_REQUIRED);
			return Promise.reject(new Error(ERROR_MSG_EMPLOYEE_DATA_REQUIRED));
		}

		const payload = {
			update_type: 'employee',
			data: cleanedData,
		};

		const CREDENTIALS = {
			url: '/update',
			data: payload,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res?.status === 'success') {
				toaster.success(res?.message || 'Employee updated successfully');
				// Return in V1 format for compatibility
				return {
					data: res.data,
				};
			}
			// If status is not success, it's an error, reject the promise
			return Promise.reject(res);
		});
	};

/**
 * Update Employee Status V2
 * @param {Object} params
 * @param {number} params.id - Employee ID
 * @param {boolean} params.is_active - New status (true for Active, false for Inactive)
 * @returns {Promise} API response
 */
export const updateEmployeeStatusV2 =
	({ id, is_active }) =>
	async () => {
		if (!id) {
			toaster.error(ERROR_MSG_EMPLOYEE_ID_REQUIRED_STATUS);
			return Promise.reject(new Error(ERROR_MSG_EMPLOYEE_ID_REQUIRED_STATUS));
		}

		const payload = {
			update_type: 'employee_status',
			data: {
				id,
				is_active: Boolean(is_active),
			},
		};

		const CREDENTIALS = {
			url: '/update',
			data: payload,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res?.status === 'success') {
				toaster.success(
					res?.message ||
						`Employee ${is_active ? 'activated' : 'deactivated'} successfully`
				);
			}
			return res;
		});
	};

/**
 * Helper: Validate and process profile_filename
 * @param {string|undefined|null} profile_filename - Profile filename
 * @returns {string|null} Validated filename or null
 */
const validateProfileFilename = (profile_filename) => {
	if (profile_filename === undefined || profile_filename === null) {
		return null;
	}

	if (typeof profile_filename !== 'string') {
		toaster.error('Profile filename must be a string');
		throw new Error('Invalid profile filename');
	}

	if (profile_filename.length > 255) {
		toaster.error('Profile filename must be 255 characters or less');
		throw new Error('Profile filename too long');
	}

	const trimmed = profile_filename.trim();
	return trimmed !== '' ? trimmed : null;
};

/**
 * Helper: Validate and process password
 * @param {string|undefined|null} password - Password
 * @returns {string|null} Validated password or null
 */
const validatePassword = (password) => {
	if (password === undefined || password === null) {
		return null;
	}

	if (typeof password !== 'string') {
		toaster.error('Password must be a string');
		throw new Error('Invalid password');
	}

	if (password.length < 6) {
		toaster.error('Password must be at least 6 characters');
		throw new Error('Password too short');
	}

	return password.trim() !== '' ? password : null;
};

/**
 * Update Employee Profile V2 (Self-Service)
 * Employee updates their own profile (limited fields: profile_filename and password)
 * Uses login_user_id from JWT token - no employee ID needed
 * @param {Object} params
 * @param {string} [params.profile_filename] - Profile picture filename (max 255 chars)
 * @param {string} [params.password] - New password (min 6 chars, optional)
 * @returns {Promise} API response with updated profile data
 */
export const updateEmployeeProfileV2 =
	({ profile_filename, password }) =>
	async () => {
		// Build data object - only include provided and valid fields
		const data = {};

		try {
			// Validate and include profile_filename if provided
			const validatedFilename = validateProfileFilename(profile_filename);
			if (validatedFilename) {
				data.profile_filename = validatedFilename;
			}

			// Validate and include password if provided
			const validatedPassword = validatePassword(password);
			if (validatedPassword) {
				data.password = validatedPassword;
			}
		} catch (error) {
			return Promise.reject(error);
		}

		// At least one field must be provided
		if (Object.keys(data).length === 0) {
			toaster.error('At least one field (profile_filename or password) must be provided');
			return Promise.reject(new Error('No fields to update'));
		}

		const payload = {
			update_type: 'employee_profile',
			data,
		};

		const CREDENTIALS = {
			url: '/update',
			data: payload,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res?.status === 'success') {
				toaster.success(res?.message || 'Employee profile updated successfully');
				return res.data;
			}
			// If status is not success, it's an error, reject the promise
			return Promise.reject(res);
		});
	};

/**
 * Get Employee Profile V2 (Self-Service)
 * Get logged-in employee's own profile
 * Uses login_user_id from JWT token - no employee ID needed
 * @returns {Promise} Employee profile object with { id, name, username, email, mobile, profile_url }
 */
export const getEmployeeProfileV2 = () => async () => {
	const CREDENTIALS = {
		url: '/search',
		data: {
			search_type: 'employee_profile',
			data: {},
		},
		version: 'v2',
	};

	return API.common(CREDENTIALS).then((response) => {
		if (response?.status === 'success' && response.data) {
			// Return profile data directly (not wrapped in array)
			return response.data;
		}
		// If status is not success, it's an error, reject the promise
		return Promise.reject(response);
	});
};

/**
 * Get Employee by ID V2
 * Uses search endpoint with id filter
 * @param {Object} params
 * @param {string|number} params.id - Employee ID
 * @returns {Promise} Employee object or null
 */
export const getEmployeeByIdV2 =
	({ id }) =>
	async () => {
		if (!id) {
			return Promise.reject(new Error('Employee ID is required'));
		}

		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'employee',
				filter_by: {
					id,
				},
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			if (response?.status === 'success' && Array.isArray(response.data)) {
				return response.data.length > 0 ? response.data[0] : null;
			}
			return null;
		});
	};

/**
 * Get Employee Filters V2
 * Fetches filter options (cities, clients, projects) for dropdowns
 * @param {Object} params
 * @param {Array} params.filter_list - Array of filter types to fetch: ['cities', 'clients', 'projects']
 * @returns {Promise} Object with { cities, clients, projects }
 */
export const getEmployeeFiltersV2 =
	({ filter_list = ['cities', 'clients', 'projects'] }) =>
	async () => {
		const CREDENTIALS = {
			url: '/list_search',
			data: {
				list_search_type: 'employee',
				filter_list,
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			if (response?.status === 'success' && response.data) {
				const { cities = [], clients = {}, projects = {} } = response.data;

				// Transform cities array to options format
				const citiesOptions = Array.isArray(cities)
					? cities.map((city) => ({
							value: city,
							label: city,
					  }))
					: [];

				// Transform clients object to options format
				const clientsOptions = transformObjectToOptions(clients);

				// Transform projects object to options format
				const projectsOptions = transformObjectToOptions(projects);

				return {
					cities: citiesOptions,
					clients: clientsOptions,
					projects: projectsOptions,
				};
			}

			return {
				cities: [],
				clients: [],
				projects: [],
			};
		});
	};

/**
 * Get User Types V2
 * Returns static list of user types (V2 API doesn't support user_type search)
 * @returns {Promise} Array of user type options { value, label }
 */
export const getUserTypesV2 = () => async () =>
	// V2 API doesn't support search_type: 'user_type'
	// Use static list from config to avoid V1 API calls
	// Note: This function is kept for backward compatibility but not used
	// The form now uses empUserTypesOptions directly from config
	Promise.resolve([]);

/**
 * NOTE: V2 API does not support search_type: 'location'
 * Location data (zones, states, cities) must be fetched using V1 API
 * even when "New UI" is active, as this is a limitation of the V2 API.
 * This function is kept for reference but should not be used.
 */

export default employeeSliceV2.reducer;
